import Icon from "components/Icons/Icon";
import { motion, Variants } from "framer-motion";
import { t } from "i18n/index";
import { buildLocalizedPath, hasLocalisedRoute } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import { useState } from "react";
import useReactRouter from "use-react-router";
import { FeatureMenuItem, getExperienceFeatures, getPlanFeatures, getReliveFeatures } from "./featureMenuItems";

const itemVariants: Variants = {
	open: {
		opacity: 1,
		y: 0,
		transition: { type: "spring", stiffness: 300, damping: 24 },
	},
	closed: { opacity: 0, y: 20, transition: { duration: 0.2 } },
};

type Props = {
	label?: string;
	translations?: Record<string, string>;
};

const FeatureMenu: React.FC<Props> = ({ label = "Product", translations = {} }) => {
	const { match } = useReactRouter();
	const [isOpen, setIsOpen] = useState(false);
	// 123yb692pu8: every link in this dropdown was un-prefixed, so a reader on /es/… who opened
	// the Product menu landed back in English. Same fix and same `lang` source as Navigation.tsx,
	// which mounts this component — the desktop dropdown is a second surface rendering the same
	// feature URLs, and fixing only the nav would have left the reported repro in place.
	//
	// Gated on `hasLocalisedRoute` the same way Navigation.tsx does, the same predicate now says
	// yes for all of these feature URLs (itinerary planner, esim, ...) — phase 3 step 1 of
	// work/locale-coverage-unification.md gave them real (if `noindex`) locale routes — so every
	// open of this dropdown correctly prefixes them. The gate stays: `hasLocalisedRoute` is what
	// keeps this component from prefixing a link the day it's wrong again, without editing here.
	const lang = useMarketingLang();
	const localizedHref = (path: string) => (hasLocalisedRoute(path) ? buildLocalizedPath(path, lang) : path);

	const renderListItem = (item: FeatureMenuItem) => (
		<motion.li variants={itemVariants}>
			<a href={localizedHref(item.url)} className={`${match?.path === item?.url ? "active" : ""} ${item?.color}`}>
				<span className={`icon-badge icon-badge--${item.color}`}>
					<Icon name={item.icon} fill="currentColor" />
				</span>
				{item.label}
			</a>
		</motion.li>
	);

	return (
		<motion.div initial={false} animate={isOpen ? "open" : "closed"} className="dropdown-menu" onMouseLeave={() => setIsOpen(false)}>
			<motion.div whileTap={{ scale: 0.97 }} onMouseEnter={() => setIsOpen(true)} className="dropdown-menu__label">
				{label}
				<motion.div
					variants={{
						open: { rotate: 180 },
						closed: { rotate: 0 },
					}}
					transition={{ duration: 0.2 }}
					style={{ originY: 0.48, originX: 0.68 }}
				>
					<Icon name="chevron-down" size={10} />
				</motion.div>
			</motion.div>
			<motion.div
				className="dropdown-menu__card"
				variants={{
					open: {
						opacity: 1,
						translateY: 26,
						translateX: "-50%",
						transition: {
							type: "spring",
							bounce: 0.4,
							duration: 1,
							delayChildren: 0.04,
							staggerChildren: 0.04,
						},
					},
					closed: {
						opacity: 0,
						translateY: 60,
						translateX: "-50%",
						transition: {
							type: "spring",
							bounce: 0,
							duration: 0.3,
						},
					},
				}}
				style={{ pointerEvents: isOpen ? "auto" : "none" }}
			>
				<div className="column">
					<h4>{t(translations, "nav.plan", "Plan")}</h4>
					<ul>{getPlanFeatures(translations).map((item) => renderListItem(item))}</ul>
				</div>
				<div className="column">
					<h4>{t(translations, "nav.experience", "Experience")}</h4>
					<ul>{getExperienceFeatures(translations).map((item) => renderListItem(item))}</ul>
				</div>
				<div className="column">
					<h4>{t(translations, "nav.shareRelive", "Share & Relive")}</h4>
					<ul>{getReliveFeatures(translations).map((item) => renderListItem(item))}</ul>
				</div>
			</motion.div>
		</motion.div>
	);
};

export default FeatureMenu;
